import React from "react";
import {
  Breadcrumbs as MuiBreadcrumbs,
  Link,
  Typography,
  Box,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { brand, gray } from "../../theme/themePrimitives";
import { useCustomisation } from "../../context/CustomisationContext";
import { PreviewChip } from "../PageHeader/PageHeader";
import { isModifiedClick } from "../../utils/navigationClick";

export interface BreadcrumbItem {
  label: string;
  path?: string;
  state?: unknown;
  onClick?: () => void;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  previewText?: string;
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  previewText,
}) => {
  const { customisations } = useCustomisation();
  const location = useLocation();
  const isHomeRoute =
    location.pathname === "/home" || location.pathname.startsWith("/home/");

  // matches "/assignment/<id>" only (no extra segments)
  const isAssignmentDetailsRoute = /^\/assignment\/[^/]+$/.test(
    location.pathname,
  );

  const isHomePage = isHomeRoute || isAssignmentDetailsRoute;

  const {
    HOME: { buttonsSettings: homeButtonColors },
  } = customisations.customisations;

  const homePrimaryButtonBgColor = homeButtonColors?.homeBtnPrimaryBgColor;
  const homeLinkButtonColor = homeButtonColors?.homeLinkColor;

  const linkStyle = {
    color: gray[600],
    textDecoration: "none !important",
    fontWeight: 500,
  };

  return (
    <MuiBreadcrumbs
      aria-label="breadcrumb"
      separator={
        <NavigateNextIcon fontSize="small" sx={{ color: gray[300] }} />
      }
      sx={{
        "& .MuiBreadcrumbs-ol": {
          flexWrap: "nowrap",
        },
        paddingBottom: 2,
      }}
    >
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const isFirst = index === 0;
        const isNonClickable =
          (isFirst && !item.onClick) || (!item.path && !item.onClick);

        if (isLast) {
          return (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", gap: 2 }}
            >
              <Typography
                sx={{
                  color: isHomePage ? homePrimaryButtonBgColor : brand[600],
                  fontWeight: 600,
                  border: `1px solid ${isHomePage ? homePrimaryButtonBgColor : brand[600]}`,
                  borderRadius: "6px",
                  padding: "4px 8px",
                  cursor: "default",
                }}
              >
                {item.label}
              </Typography>
              {previewText && <PreviewChip label={previewText} />}
            </Box>
          );
        }

        if (isNonClickable) {
          return (
            <Typography key={index} sx={{ ...linkStyle, cursor: "default" }}>
              {item.label}
            </Typography>
          );
        }

        // If onClick is provided without a path, use a clickable Typography
        // instead of Link — there's nothing to link to.
        if (item.onClick && !item.path) {
          return (
            <Typography
              key={index}
              onClick={item.onClick}
              sx={{
                ...linkStyle,
                color: isHomePage ? homeLinkButtonColor : gray[600],
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline !important",
                },
              }}
            >
              {item.label}
            </Typography>
          );
        }

        // Both onClick and path: render as a real Link so Ctrl/Cmd/middle
        // click can open it in a new tab, but keep running the custom
        // onClick side effects for a plain click.
        if (item.onClick && item.path) {
          return (
            <Link
              key={index}
              component={RouterLink}
              to={item.path}
              state={item.state}
              onClick={(e) => {
                if (isModifiedClick(e)) return;
                e.preventDefault();
                item.onClick!();
              }}
              sx={{
                ...linkStyle,
                color: isHomePage ? homeLinkButtonColor : gray[600],
                "&::before": {
                  display: "none",
                },
                "&:hover": {
                  textDecoration: "underline !important",
                },
              }}
            >
              {item.label}
            </Link>
          );
        }

        return (
          <Link
            key={index}
            component={RouterLink}
            to={item.path!}
            state={item.state}
            sx={{
              ...linkStyle,
              color: isHomePage ? homeLinkButtonColor : brand[600],
              "&::before": {
                display: "none",
              },
              "&:hover": {
                textDecoration: "underline !important",
              },
            }}
          >
            {item.label}
          </Link>
        );
      })}
    </MuiBreadcrumbs>
  );
};
